/**
 * JourneyProvider — the journey engine.
 *
 * Mounted once inside TenantBoundary so it survives every route change.
 * Inert (renders children only, attaches nothing) unless ALL hold:
 *   - modules.journey is on for the tenant
 *   - someone is signed in (every role has a journey — their own)
 *   - the viewport is desktop-width (the limited-world metaphor needs
 *     the sidebar; phones keep the product untouched)
 *
 * Engine rules ported from Signal:
 *   - The current step is the first incomplete step of the eligible chain.
 *   - A detection matching the CURRENT step completes it (+3 Neurons).
 *   - A detection matching a LATER eligible step completes it at +5
 *     ("discovered on your own") — exploration is never punished.
 *   - "Do it for me" (offered by the dock after 30 s on a step) completes
 *     at +1 and navigates where needed. Never traps.
 *   - Skip ends the journey instantly and is offered on every surface.
 *   - NOTHING is ever withheld. The journey shows people around a platform
 *     they already have; it has never gated access, and as of 2026-09-12 it
 *     does not dress anything up as gated either. No padlocks, no dimming,
 *     no "complete a chapter to reach this".
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@modules/auth';
import { useFeature } from '@/hooks/useFeature';
import {
  audienceForRole,
  deriveChain,
  deriveRank,
  NEURONS_ASSISTED,
  NEURONS_EXPLORED,
  NEURONS_TRY,
  type EligibleStep,
  type JourneyAudience,
  type Rank,
} from '../config/journeyConfig';
import { onJourneyEvent, type JourneyEventName } from '../detect';
import {
  EMPTY_JOURNEY,
  flushJourneyState,
  loadJourneyState,
  saveJourneyState,
  type JourneyPersisted,
} from '../services/journeyState';

/** Transient reward surfaced by the toast layer. */
export interface JourneyReward {
  id: number;
  neurons: number;
  kind: 'try' | 'assisted' | 'explored';
  rankUp: Rank | null;
}

export interface JourneyContextValue {
  active: boolean;
  chain: EligibleStep[];
  stages: { id: number; slug: string; steps: EligibleStep[] }[];
  currentStep: EligibleStep | null;
  completed: Set<string>;
  neurons: number;
  rank: Rank;
  finished: boolean;
  skipped: boolean;
  /** Welcome map should be showing. */
  showWelcome: boolean;
  /** Finale map should be showing. */
  showFinale: boolean;
  /** Film popup open for this step id (null = closed). */
  filmOpenFor: string | null;
  rewards: JourneyReward[];
  begin: () => void;
  dismissWelcome: () => void;
  dismissFinale: () => void;
  skipAll: () => void;
  /** Wipe progress and start the tour over from the welcome map. */
  restart: () => void;
  skipStep: () => void;
  openFilm: (stepId: string) => void;
  closeFilm: (watched: boolean) => void;
  doItForMe: () => void;
  /** Complete the current `notice` step (it has no gesture to detect). */
  acknowledge: () => void;
  consumeReward: (id: number) => void;
  /** True while a step's film has not been watched yet. */
  needsWatch: (step: EligibleStep) => boolean;
}

const JourneyContext = createContext<JourneyContextValue | null>(null);

/** Null-safe consumer: surfaces outside an active journey get an inert view. */
export function useJourney(): JourneyContextValue | null {
  return useContext(JourneyContext);
}


const DESKTOP_MIN_WIDTH = 900;
const WELCOME_SETTLE_MS = 1500;

export function JourneyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const journeyEnabled = useFeature('modules.journey');
  // Every chain opens on the agent gallery — ORIENT's ring anchors to a card
  // in it — so a tenant without the Agents module would start a tour whose
  // first step can never be reached. Defaults true and is the platform
  // baseline everywhere else, but three sibling flags are gated below and
  // leaving the fourth implicit is the kind of asymmetry that becomes a bug
  // the first time somebody turns it off.
  const agentsEnabled = useFeature('modules.agents');
  const libraryOn = useFeature('modules.library');
  const aoaOn = useFeature('modules.agent_of_agents');
  const projectsOn = useFeature('modules.projects');
  const insightsOn = useFeature('modules.insights');

  const featureMap: Record<string, boolean> = useMemo(
    () => ({
      'modules.library': libraryOn,
      'modules.agent_of_agents': aoaOn,
      'modules.projects': projectsOn,
      'modules.insights': insightsOn,
    }),
    [libraryOn, aoaOn, projectsOn, insightsOn],
  );

  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH,
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Every role has a journey now — their OWN journey. The chain is picked by
  // role and then pruned by the tenant's flags, so a member is never taught
  // a control they do not have and an admin is never shown a tour written
  // for someone else.
  const audience: JourneyAudience = audienceForRole(user?.role);
  const active = journeyEnabled && agentsEnabled && Boolean(user) && isDesktop;

  const { steps: chain, stages } = useMemo(
    () => deriveChain(audience, (flag) => featureMap[flag] ?? false),
    [audience, featureMap],
  );

  const [state, setState] = useState<JourneyPersisted>({ ...EMPTY_JOURNEY });
  const [loaded, setLoaded] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showFinale, setShowFinale] = useState(false);
  const [filmOpenFor, setFilmOpenFor] = useState<string | null>(null);
  const [rewards, setRewards] = useState<JourneyReward[]>([]);
  const rewardSeq = useRef(0);

  // Load persisted state once the journey is active.
  useEffect(() => {
    if (!active || loaded) return;
    let cancelled = false;
    void loadJourneyState()
      .then((s) => {
        if (cancelled) return;
        stateRef.current = s;
        setState(s);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true); // engine still runs; state starts empty
      });
    return () => {
      cancelled = true;
    };
  }, [active, loaded]);

  const completed = useMemo(() => new Set(state.completed), [state.completed]);
  // A skipped journey has NO current step. Before this, skipping left
  // `currentStep` pointing at the next uncompleted step, so the glow ring kept
  // circling the upload button in every later conversation — the tour had
  // been declined and was still pointing at things. Nulling it here ends
  // every consumer at once: the ring, the notice card, the film popup.
  const currentStep = useMemo(
    () => (state.skipped ? null : chain.find((s) => !completed.has(s.id)) ?? null),
    [chain, completed, state.skipped],
  );
  // Finished means every step was actually completed — NOT "there is no
  // current step", which is also true of a skipped tour and would have handed
  // a skipper the Mastermind rank for declining.
  const finished = loaded && chain.length > 0 && chain.every((s) => completed.has(s.id));
  // Completing the chain IS Mastermind, whatever the Neuron total: an
  // assisted (+1) step must never leave the final rank unreachable.
  const rank = useMemo(
    () => (finished ? 'mastermind' : deriveRank(state.neurons, chain)),
    [finished, state.neurons, chain],
  );

  // Welcome moment: first activation, nothing done, not skipped — after a settle.
  useEffect(() => {
    if (!active || !loaded || state.skipped || state.startedAt || finished) return;
    const t = setTimeout(() => setShowWelcome(true), WELCOME_SETTLE_MS);
    return () => clearTimeout(t);
  }, [active, loaded, state.skipped, state.startedAt, finished]);

  /**
   * The authoritative state for anything happening in THIS tick.
   *
   * React's `state` only updates on the next render, and journey events can
   * fire several times in one synchronous call stack — ChatInput emits
   * `messageSent` and then `fileAttached` back to back when a message goes
   * out with an attachment. Two completions computed from the same stale
   * `state` produce two `setState` calls where the second overwrites the
   * first: both reward toasts appear, but only one step is actually recorded.
   * The user is told they earned something the app then forgets.
   *
   * Every writer goes through `persist`, which updates this ref first, so a
   * second completion in the same tick composes onto the first instead of
   * clobbering it.
   */
  const stateRef = useRef<JourneyPersisted>(state);

  const persist = useCallback((next: JourneyPersisted) => {
    stateRef.current = next;
    setState(next);
    saveJourneyState(next);
  }, []);

  const pushReward = useCallback(
    (neurons: number, kind: JourneyReward['kind'], rankUp: Rank | null) => {
      const id = ++rewardSeq.current;
      setRewards((r) => [...r, { id, neurons, kind, rankUp }]);
    },
    [],
  );

  const completeStep = useCallback(
    (step: EligibleStep, kind: JourneyReward['kind']) => {
      // Read the ref, never the closure: this may be the second completion in
      // one tick, and the guard has to see the first one's result too.
      const current = stateRef.current;
      if (current.completed.includes(step.id)) return;
      const gain =
        kind === 'try' ? NEURONS_TRY : kind === 'assisted' ? NEURONS_ASSISTED : NEURONS_EXPLORED;
      const before = deriveRank(current.neurons, chain);
      const nextNeurons = current.neurons + gain;
      const nextCompleted = [...current.completed, step.id];
      const isLast = chain.every((s) => nextCompleted.includes(s.id));
      const after = isLast ? 'mastermind' : deriveRank(nextNeurons, chain);
      const next: JourneyPersisted = {
        ...current,
        neurons: nextNeurons,
        completed: nextCompleted,
        startedAt: current.startedAt ?? new Date().toISOString(),
        completedAt: isLast ? new Date().toISOString() : current.completedAt,
      };
      persist(next);
      pushReward(gain, kind, after !== before ? after : null);
      if (isLast) {
        flushJourneyState();
        setShowFinale(true);
      }
    },
    [chain, persist, pushReward],
  );

  // Detection listeners — one per distinct event in the chain.
  useEffect(() => {
    if (!active || !loaded || state.skipped || finished) return;
    const names = new Set<JourneyEventName>(
      chain.map((s) => s.detection).filter((d): d is JourneyEventName => Boolean(d)),
    );
    const offs: (() => void)[] = [];
    for (const name of names) {
      offs.push(
        onJourneyEvent(name, () => {
          // Current step first; else exploration credit for a later step.
          const cur = chain.find((s) => !new Set(state.completed).has(s.id));
          if (cur && cur.detection === name) {
            completeStep(cur, 'try');
            return;
          }
          const later = chain.find(
            (s) => s.detection === name && !state.completed.includes(s.id),
          );
          if (later) completeStep(later, 'explored');
        }),
      );
    }
    return () => offs.forEach((off) => off());
  }, [active, loaded, state.skipped, state.completed, finished, chain, completeStep]);

  const begin = useCallback(() => {
    setShowWelcome(false);
    const current = stateRef.current;
    persist({ ...current, startedAt: current.startedAt ?? new Date().toISOString() });
    const first = chain[0];
    if (first && first.kind === 'film' && !stateRef.current.watched.includes(first.id)) {
      setFilmOpenFor(first.id);
    }
  }, [chain, persist]);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    // Stamp startedAt so the welcome never re-ambushes; journey stays
    // resumable from the dock.
    const current = stateRef.current;
    persist({ ...current, startedAt: current.startedAt ?? new Date().toISOString() });
  }, [persist]);

  const dismissFinale = useCallback(() => setShowFinale(false), []);

  const restart = useCallback(() => {
    // Back to zero: no progress, no start stamp, not skipped. The welcome
    // effect below sees a journey that has never begun and brings the map
    // back on its own; the finale is closed here in case it was up.
    setShowFinale(false);
    setFilmOpenFor(null);
    persist({ ...EMPTY_JOURNEY });
    // Flush rather than let the 800ms debounce carry it: restarting navigates
    // away immediately, and a tab closed inside that window would bring the
    // old progress back.
    flushJourneyState();
  }, [persist]);

  const skipAll = useCallback(() => {
    setShowWelcome(false);
    setFilmOpenFor(null);
    const current = stateRef.current;
    persist({ ...current, skipped: true, startedAt: current.startedAt ?? new Date().toISOString() });
    flushJourneyState();
  }, [persist]);

  const openFilm = useCallback((stepId: string) => setFilmOpenFor(stepId), []);

  const closeFilm = useCallback(
    (watched: boolean) => {
      setFilmOpenFor((openId) => {
        const current = stateRef.current;
        if (watched && openId && !current.watched.includes(openId)) {
          persist({ ...current, watched: [...current.watched, openId] });
        }
        return null;
      });
    },
    [persist],
  );

  const doItForMe = useCallback(() => {
    if (currentStep) completeStep(currentStep, 'assisted');
  }, [currentStep, completeStep]);

  /**
   * Move past the step in front of you without doing it.
   *
   * It marks the step done so the chain advances, but pays NOTHING — the
   * Neurons are for doing the thing, and quietly awarding them for stepping
   * over it would make the rank meaningless. "Do it for me" still exists and
   * still pays a little, for someone who wants the result without the
   * gesture; this is for someone who simply does not want this step at all.
   * Not offered on a notice: reading one line is not an imposition, and
   * acknowledging it is already the whole step.
   */
  const skipStep = useCallback(() => {
    if (!currentStep || currentStep.kind === 'notice') return;
    const current = stateRef.current;
    if (current.completed.includes(currentStep.id)) return;
    const nextCompleted = [...current.completed, currentStep.id];
    const isLast = chain.every((s) => nextCompleted.includes(s.id));
    persist({
      ...current,
      completed: nextCompleted,
      startedAt: current.startedAt ?? new Date().toISOString(),
      completedAt: isLast ? new Date().toISOString() : current.completedAt,
    });
    setFilmOpenFor(null);
    if (isLast) { flushJourneyState(); setShowFinale(true); }
  }, [currentStep, chain, persist]);

  /**
   * Acknowledge a `notice` step. It asks for nothing, so reading it IS the
   * step — and it pays the full award, because being told what you
   * administer is not a lesser thing than clicking a button.
   */
  const acknowledge = useCallback(() => {
    if (currentStep?.kind === 'notice') completeStep(currentStep, 'try');
  }, [currentStep, completeStep]);

  const consumeReward = useCallback((id: number) => {
    setRewards((r) => r.filter((x) => x.id !== id));
  }, []);

  const needsWatch = useCallback(
    (step: EligibleStep) => step.kind === 'film' && !state.watched.includes(step.id),
    [state.watched],
  );

  const value: JourneyContextValue = useMemo(
    () => ({
      active: active && loaded,
      chain,
      stages,
      currentStep,
      completed,
      neurons: state.neurons,
      rank,
      finished,
      skipped: state.skipped,
      showWelcome,
      showFinale,
      filmOpenFor,
      rewards,
      begin,
      dismissWelcome,
      dismissFinale,
      skipAll,
      restart,
      skipStep,
      openFilm,
      closeFilm,
      doItForMe,
      acknowledge,
      consumeReward,
      needsWatch,
    }),
    [
      active, loaded, chain, stages, currentStep, completed, state.neurons, rank,
      finished, state.skipped, showWelcome, showFinale, filmOpenFor,
      rewards, begin, dismissWelcome, dismissFinale, skipAll, restart, skipStep, openFilm,
      closeFilm, doItForMe, acknowledge, consumeReward, needsWatch,
    ],
  );

  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}
